<template>
  <validation-provider
    v-slot="{ errors, failed }"
    ref="dateProvider"
    :vid="`${vid}_date`"
    :rules="{
      dateInputs: {
        yearsRange: yearsRange,
      },
    }"
  >
    <input
      type="hidden"
      :value="date"
    >
    <div class="date-input-holder">
      <div class="date-input day col-4 px-0 centered-input-text">
        <qz-number-input
          v-model.number="date.day"
          name="dayInput"
          min="1"
          max="31"
          :data-test="`${vid}_day`"
          :error="failed"
          placeholder="DD"
          :maxlength="inputCharsLimiters.DAY"
          :without-separators="true"
          @keyup="moveToNextField"
          @blur="onInputBlur($event, 'day')"
          @paste.prevent
        />
      </div>
      <div class="date-input month no-left-border col-4 px-0 centered-input-text">
        <qz-number-input
          ref="monthInput"
          v-model.number="date.month"
          name="monthInput"
          min="1"
          max="12"
          :data-test="`${vid}_month`"
          :error="failed"
          placeholder="MM"
          :maxlength="inputCharsLimiters.MONTH"
          :without-separators="true"
          @keyup="moveToNextField"
          @blur="onInputBlur($event, 'month')"
          @paste.prevent
        />
      </div>
      <div class="date-input year col-4 px-0 centered-input-text">
        <qz-number-input
          ref="yearInput"
          v-model.number="date.year"
          :min="yearsRange.min"
          :max="yearsRange.max"
          :data-test="`${vid}_year`"
          :error="failed"
          placeholder="YYYY"
          :maxlength="inputCharsLimiters.YEAR"
          :without-separators="true"
          @blur="onInputBlur($event, 'year')"
          @paste.prevent
        />
      </div>
    </div>
    <div
      v-if="failed"
      class="error--text mt-2 px-3 fz12"
    >
      {{ errors[0] }}
    </div>
  </validation-provider>
</template>

<script>
import {
  padStart,
} from 'lodash';

import {
  ExtDate,
  checkDateObjFilled,
} from '@/helpers/date';

import {
  ref,
  inject,
  computed,
  watch,
} from '@vue/composition-api';
import useDateSelect from '@/composables/useDateSelect.js';

export default {
  name: 'qz-date-input',
  model: {
    prop: 'value',
    event: 'date-change',
  },
  props: {
    vid: {
      type: String,
      required: true,
    },
    yearOptions: {
      type: Array,
      required: true,
    },
    value: {
      type: String,
      default: null,
    },
  },
  setup (props, { emit }) {
    // INJECTS
    const inputCharsLimiters = inject('inputCharsLimiters');

    // COMPOSABLES
    const {
      convertDateObjToDate,
      getDateObj,
    } = useDateSelect();

    // TEMPLATE REFS
    const dateProvider = ref(null);
    const monthInput = ref(null);
    const yearInput = ref(null);

    // DATA
    const date = ref({});
    const parsedDate = ref(null);

    // COMPUTED
    const dateObject = computed(() => {
      if (props.value === null) return null;
      return getDateObj(new Date(props.value));
    });
    const yearsRange = computed(() => {
      return {
        max: Math.max(...props.yearOptions),
        min: Math.min(...props.yearOptions),
      };
    });

    // METHODS
    const moveToNextField = (ev) => {
      const value = ev.target.value;
      const isDigit = ev.keyCode >= 48 && ev.keyCode <= 57 && isFinite(Number(ev.key));
      if (isDigit && value.length === 2) {
        ev?.target?.name === 'dayInput' ? monthInput.value.focus() : yearInput.value.focus();
      }
    };
    /**
     * Clear input if value is 0
     * Day Input: Add 0 before value if day value is not two-digit number
     * @param event
     * @param field: name of the field (day | month | year)
    */
    const onInputBlur = (event, field) => {
      let inputValue = +event.target.value;
      inputValue = inputValue === 0 ? '' : inputValue;

      if (field === 'day' && inputValue !== '') inputValue = padStart(inputValue, 2, 0);
      date.value[field] = inputValue;
    };
    const change = () => {
      const dateIsFulfilled = checkDateObjFilled(date.value);
      if (dateIsFulfilled) {
        parsedDate.value = null;
        emit('date-change', parsedDate.value);
        return;
      }

      // Check if the year is within yearsRange boundries before setting it
      if (date.value.year >= yearsRange.value.min && date.value.year <= yearsRange.value.max) {
        const dateObj = convertDateObjToDate(date.value);

        parsedDate.value = dateObj.isValid() ? dateObj.format() : null;
      } else {
        parsedDate.value = null;
      }

      emit('date-change', parsedDate.value);
    };

    // WATCHES
    watch(date, () => {
      change();
      dateProvider.value.validate();
    }, {
      deep: true,
    });

    watch(() => props.value, (value) => {
      if (value === null || value === parsedDate.value) return;

      date.value = { ...dateObject.value };
    }, {
      immediate: true,
    });

    return {
      ExtDate,

      // INJECTS
      inputCharsLimiters,

      // TEMPLATE REFS
      dateProvider,
      monthInput,
      yearInput,

      // DATA
      date,
      parsedDate,

      // COMPUTED
      yearsRange,

      // METHODS
      onInputBlur,
      moveToNextField,
    };
  },
};
</script>

<style lang="scss" scoped>
.date-input::v-deep {
  .v-input {
    input[type=number]::-webkit-outer-spin-button,
    input[type=number]::-webkit-inner-spin-button {
      appearance: auto;
      -webkit-appearance: auto;
    }

    input[type=number] {
      -moz-appearance: auto;
    }
  }

  &.day .v-text-field--outlined fieldset {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: 0;
  }

  &.month {
    margin-left: -1px;

    .v-text-field--outlined fieldset {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right: 0;
    }
  }

  &.month.no-left-border .v-text-field--outlined fieldset {
    border-radius: 0;
    border-right: 0;
    border-left: 0;
  }

  &.year {
    margin-left: -1px;

    .v-text-field--outlined fieldset {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-left: 0;
    }
  }

  .v-text-field--outlined.v-input--is-focused fieldset {
    border-radius: 4px !important;
    border: 2px solid currentColor !important;
  }
}

.centered-input-text::v-deep input {
  text-align: center;
}

.date-input-holder {
  display: flex;
  flex-wrap: nowrap;
}

.date-input + .date-input::before {
  content: "";
  position: absolute;
  border: 1px solid;
  border-left: 0;
  height: 35px;
  margin-top: 10px;
}
</style>
